import {ClusterType} from 'components/calculator/MDBForm/constants';
import _ from 'lodash';

import ComputeForm from './ComputeForm/ComputeForm';
import StorageForm from './StorageForm/StorageForm';
import SpeechKitForm from './SpeechKitForm/SpeechKitForm';
import TranslateForm from './TranslateForm/TranslateForm';
import KubernetesForm from './KubernetesForm/KubernetesForm';
import SupportPricingForm from './SupportPricingForm/SupportPricingForm';
import getMDBForm from './MDBForm/MDBForm';
import {Locale} from 'contexts/LocaleContext';
import {getSettings} from 'utils';
import GreenplumForm from 'components/calculator/GreenplumForm/GreenplumForm';
import {BannerTheme} from './Banner/Banner';

export interface CalculatorBannerConfig {
    title: string;
    content: string;
    theme: BannerTheme;
}

export interface CalculatorConfig {
    services: string[];
    banner: [CalculatorBannerConfig];
}

export interface ServiceConfig {
    id: string;
    nameKey: string;
    form: React.ComponentType<any>; // eslint-disable-line @typescript-eslint/no-explicit-any
    multi?: boolean;
}

export interface BannerConfig {
    text: string;
    start: number;
    end: number;
    url: string;
    ignorePages?: string[];
}

const commonServices = [
    'compute',
    'storage',
    'managed-kubernetes',
    'managed-greenplum',
    'speechkit',
    'translate',
    'support',
];

const mdbServices = ['postgresql', 'clickhouse', 'mongodb', 'mysql', 'redis', 'elasticsearch'];

const mdbPrefix = 'managed';

const availableServices = commonServices.concat(
    mdbServices.map((service) => `${mdbPrefix}-${service}`),
);

export const hasCalculator = (service?: string) => {
    return Boolean(
        service && (availableServices.includes(service) || mdbServices.includes(service)),
    );
};

const commonConfigs: Record<string, Omit<ServiceConfig, 'id'>> = {
    compute: {
        multi: true,
        form: ComputeForm,
        nameKey: 'label_compute',
    },
    storage: {
        form: StorageForm,
        nameKey: 'label_storage',
    },
    speechkit: {
        form: SpeechKitForm,
        nameKey: 'label_speechkit',
    },
    translate: {
        form: TranslateForm,
        nameKey: 'label_translate',
    },
    support: {
        form: SupportPricingForm,
        nameKey: 'label_support',
    },
    'managed-kubernetes': {
        form: KubernetesForm,
        nameKey: 'label_kubernetes',
    },
    'managed-greenplum': {
        form: GreenplumForm,
        nameKey: 'label_greenplum',
    },
};

const getMdbServiceConfig = (serviceName: string): ServiceConfig => {
    const service = serviceName.replace(`${mdbPrefix}-`, '');

    return {
        id: `${mdbPrefix}-${service}`,
        form: getMDBForm(service as ClusterType),
        nameKey: `label_${service}`,
    };
};

const getServiceConfig = (service: string): ServiceConfig | null => {
    if (!hasCalculator) {
        return null;
    }

    return service in commonConfigs
        ? {id: service, ...commonConfigs[service]}
        : getMdbServiceConfig(service);
};

export const getServicesConfigs = _.memoize((services: string[]) => {
    return services
        .filter(hasCalculator)
        .map((service) => getServiceConfig(service))
        .filter(Boolean) as ServiceConfig[];
});

export const getAllServicesConfigs = _.memoize(async (localeCode: Locale['code']) => {
    const {services} = (await getSettings<CalculatorConfig>('calculator', localeCode)) ?? {
        services: commonServices.concat(mdbServices),
    };

    return getServicesConfigs(services);
});

export const getCalculatorBannerConfig = _.memoize(async (localeCode: Locale['code']) => {
    const {banner} = await getSettings<CalculatorConfig>('calculator', localeCode);

    return banner;
});

export function getConfigsMap(configs: ServiceConfig[]) {
    return configs.reduce<Record<string, ServiceConfig>>((acc, config) => {
        acc[config.id] = config;
        return acc;
    }, {});
}

export const PeriodInterval = {
    hour: 60 * 60,
    day: 24 * 60 * 60,
    month: 30 * 24 * 60 * 60,
};
